"use client";

import { useEffect, useMemo, useState } from "react";
import {
  clearFsLog,
  downloadFsLog,
  isFsDebugEnabled,
  setFsDebugEnabled,
} from "@/lib/firestore-instrumented";
import { firestoreEventKind, summarizeFirestoreEvents } from "@/lib/firestoreDebug.mjs";

export default function DebugFsMount() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(isFsDebugEnabled());

    // Keyboard toggle: Ctrl/⌘ + Alt + D
    const onKey = (e) => {
      const mod = (e.ctrlKey || e.metaKey) && e.altKey && (e.key?.toLowerCase?.() === "d");
      if (!mod) return;
      const next = !isFsDebugEnabled();
      setFsDebugEnabled(next);
      location.reload();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!enabled) return null;
  return <DebugFsPanel />;
}

/* ---------- actual panel ---------- */
function DebugFsPanel() {
  const [tick, setTick] = useState(0);

  const data = useMemo(
    () => (typeof window === "undefined" ? [] : [...(window.__fslog || [])]),
    [tick]
  );

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, []);

  const counts = useMemo(() => summarizeFirestoreEvents(data), [data]);

  const clear = () => {
    clearFsLog();
    setTick((n) => n + 1);
  };
  const disable = () => { setFsDebugEnabled(false); location.reload(); };

  return (
    <div
      style={{
        position: "fixed", right: 12, bottom: 12, zIndex: 99999,
        width: 360, maxHeight: "60vh", overflow: "auto",
        background: "var(--card, #111)", color: "var(--fg, #eee)",
        border: "1px solid var(--border, #333)", borderRadius: 12, boxShadow: "0 6px 24px rgba(0,0,0,.4)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderBottom: "1px solid var(--border, #333)"}}>
        <strong>Firestore Debug</strong>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={clear} className="btn" style={btnStyle}>Clear</button>
          <button onClick={() => downloadFsLog()} className="btn" style={btnStyle}>Download</button>
          <button onClick={disable} className="btn" style={btnStyle}>Disable</button>
        </div>
      </div>

      <div style={{ padding: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
        <Stat label="active listeners" value={counts.activeListeners} />
        <Stat label="listener starts" value={counts.listenerStarts} />
        <Stat label="initial docs" value={counts.initialDocuments} />
        <Stat label="delta docs" value={counts.deltaDocuments} />
        <Stat label="one-time read docs" value={counts.readDocuments} />
        <Stat label="writes" value={counts.writes} />
        <Stat label="timed operations" value={counts.timedOperations} />
        <Stat label="average duration" value={`${counts.averageDurationMs} ms`} />
        <Stat label="cache events" value={counts.cacheEvents} />
        <Stat label="server events" value={counts.serverEvents} />
        <Stat label="other" value={counts.other} />
      </div>

      <div style={{ padding: "8px 10px", borderTop: "1px solid var(--border, #333)" }}>
        <div style={{ fontSize: 12, opacity: .8, marginBottom: 6 }}>Recent events</div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
          {data.slice(-100).reverse().map((row, i) => (
            <li key={i} style={{ padding: "6px 0", borderBottom: "1px dashed rgba(255,255,255,.08)" }}>
              <div style={{ opacity: .75 }}>{new Date(row.t || row.ts).toLocaleTimeString()}</div>
              <div>
                <b>{firestoreEventKind(row)}</b>
                {row.label ? <span> · <code>{row.label}</code></span> : null}
                {row.path ? <span> · <code>{row.path}</code></span> : null}
                {typeof row.size === "number" ? <span> · size={row.size}</span> : null}
                {typeof row.durationMs === "number" ? <span> · {row.durationMs} ms</span> : null}
                {row.source ? <span> · {row.source}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid var(--border, #333)", borderRadius: 8, padding: 8 }}>
      <div style={{ fontSize: 11, opacity: .7 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

const btnStyle = {
  background: "transparent",
  color: "inherit",
  border: "1px solid var(--border, #333)",
  borderRadius: 6,
  padding: "4px 8px",
  fontSize: 12,
  cursor: "pointer"
};
